import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import JsonLd, { webAppSchema } from 'shared/JsonLd';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';

const OCCUPANCY = [
    { v: 'r2', label: 'Multifamily (Group R-2)' },
    { v: 'mixed', label: 'Mixed-use' },
    { v: 'commercial', label: 'Commercial / Office' },
    { v: 'assembly', label: 'Assembly / Institutional / Other' },
    { v: 'open-garage', label: 'Open parking garage' },
    { v: 'r3', label: 'Single-family, duplex, or townhome (Group R-3)' },
];

const AREA_OPTIONS = [
    { v: '<50k', label: 'Under 50,000 sq ft' },
    { v: '50k+', label: '50,000 sq ft or more' },
];

// Scoped, locally-prefixed styles for this page's picker controls — a flowing
// pill group for the longer occupancy list and a joined toggle bar for the
// binary floor-area choice. Kept local to this file (derc__ prefix) so it
// doesn't collide with other pages' design systems.
const dercCss = `
.derc__opts{display:flex;flex-wrap:wrap;gap:9px}
.derc__chip{background:#F7FAFD;border:1.5px solid #D7E2F1;border-radius:999px;padding:10px 16px;cursor:pointer;font-family:'Axiforma SemiBold',sans-serif;color:#16203C;font-size:13.5px;transition:.15s;text-align:left}
.derc__chip:hover{border-color:#B9CBEA;background:#fff}
.derc__chip[aria-pressed=true]{border-color:#0044EB;background:#0044EB;color:#fff}
.derc__seg{display:inline-flex;flex-wrap:wrap;background:#F1F5FB;border:1.5px solid #D7E2F1;border-radius:13px;padding:4px;gap:3px}
.derc__segbtn{border:none;background:transparent;border-radius:9px;padding:9px 17px;font-family:'Axiforma SemiBold',sans-serif;font-size:13.5px;color:#54648A;cursor:pointer;transition:.15s;white-space:nowrap}
.derc__segbtn:hover:not([aria-pressed=true]){color:#16203C}
.derc__segbtn[aria-pressed=true]{background:#fff;color:#0044EB;box-shadow:0 3px 10px rgba(6,16,54,.12)}
`;

// Flowing single-select pills — for the longer, longer-labeled occupancy list.
const DercChips = ({ options, value, onChange, groupLabel }) => (
    <div className="derc__opts" role="group" aria-label={groupLabel}>
        {options.map((o) => (
            <button type="button" key={o.v} className="derc__chip"
                aria-pressed={value === o.v} onClick={() => onChange(o.v)}>{o.label}</button>
        ))}
    </div>
);

// Joined toggle bar — for the short, binary floor-area choice.
const DercSegmented = ({ options, value, onChange, groupLabel }) => (
    <div className="derc__seg" role="group" aria-label={groupLabel}>
        {options.map((o) => (
            <button type="button" key={o.v} className="derc__segbtn"
                aria-pressed={value === o.v} onClick={() => onChange(o.v)}>{o.label}</button>
        ))}
    </div>
);

const DasErccsCheckerPage = () => {
    usePageSeo({
        title: 'DAS / ERRCS Requirement Checker — IFC/CFC 510',
        description: 'Free ERRCS / DAS requirement checker. See if your building needs an Emergency Responder Radio Coverage System under IFC/CFC Section 510 — by occupancy and size.',
        keywords: 'DAS ERCCS requirement, ERRCS required, IFC 510, California Fire Code 510, emergency responder radio coverage, public safety DAS, in-building radio coverage, BUILDLAB fire life safety',
    });

    const [occupancy, setOccupancy] = useState('r2');
    const [area, setArea] = useState('50k+'); // '<50k' | '50k+'
    const [subterranean, setSubterranean] = useState(false);

    const result = useMemo(() => {
        if (occupancy === 'r3') {
            return { status: 'exempt', reason: 'Group R-3 occupancies (single-family homes, duplexes, and townhomes) are exempt from IFC/CFC §510.' };
        }
        if (occupancy === 'open-garage' && !subterranean) {
            return { status: 'exempt', reason: 'Open parking garages with no subterranean portions are exempt from the requirement.' };
        }
        if (area === '<50k' && !subterranean) {
            return { status: 'exempt', reason: 'Buildings with a floor area of 50,000 sq ft or less and no subterranean levels are exempt from IFC/CFC §510.' };
        }
        return {
            status: 'required',
            reason: subterranean
                ? 'Subterranean levels trigger IFC/CFC §510 regardless of size. A radio coverage test is required.'
                : 'At 50,000 sq ft or more, this project is subject to IFC/CFC §510. A radio coverage test is required.',
        };
    }, [occupancy, area, subterranean]);

    const exempt = result.status === 'exempt';

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'DAS / ERRCS Requirement Checker'} pagesub={'Checker'} pageTop={'Fire & Life Safety'} />
                <JsonLd data={webAppSchema({ name: 'DAS / ERRCS Requirement Checker', description: 'Free ERRCS / DAS requirement checker for emergency responder radio coverage under IFC/CFC Section 510.', path: '/das-erccs-checker' })} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">Does your building need an emergency responder radio coverage system?</h2>
                                <p>
                                    Under IFC/CFC §510, most new buildings must provide in-building radio coverage for emergency
                                    responders — typically delivered by a <strong>DAS (Distributed Antenna System)</strong> or
                                    <strong> ERRCS/ERCCS</strong> (also called <strong>ERCES</strong> under NFPA 1225). Answer three
                                    questions to see whether your project is likely subject to the requirement.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Inputs */}
                            <div className="col-lg-7 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '24px 28px' }}>
                                    <style dangerouslySetInnerHTML={{ __html: dercCss }} />
                                    <div className="mb-3">
                                        <label className="input_title d-block mb-1">Occupancy / building type</label>
                                        <DercChips options={OCCUPANCY} value={occupancy} onChange={setOccupancy} groupLabel="Occupancy type" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="input_title d-block mb-1">Total building floor area</label>
                                        <DercSegmented options={AREA_OPTIONS} value={area} onChange={setArea} groupLabel="Building floor area" />
                                    </div>
                                    <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', fontSize: '.92rem' }}>
                                        <input type="checkbox" checked={subterranean} onChange={() => setSubterranean((v) => !v)} />
                                        Has <strong>subterranean levels</strong> (basement or underground parking)
                                    </label>
                                </div>
                            </div>

                            {/* Verdict */}
                            <div className="col-lg-5 mb-4">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '28px 30px', color: '#fff', height: '100%' }}>
                                    <div style={{ fontSize: '.85rem', opacity: 0.8 }}>IFC/CFC §510 assessment</div>
                                    <div style={{ fontSize: '1.7rem', fontWeight: 800, lineHeight: 1.15, margin: '4px 0 10px', color: exempt ? '#7CFFB2' : '#FFC9A3' }}>
                                        {exempt ? 'Likely exempt' : 'Likely required'}
                                    </div>
                                    <p style={{ fontSize: '.9rem', opacity: 0.92 }}>{result.reason}</p>
                                    {!exempt && (
                                        <p style={{ fontSize: '.84rem', opacity: 0.8 }}>
                                            Coverage standard: −95 dBm signal across 95% of the building and all critical areas
                                            (command center, fire pump room, exits, stairs, elevator lobbies). If the building fails a
                                            radio coverage test, an ERRCS must be installed with a 24-hour backup power source.
                                        </p>
                                    )}
                                    <Link to="/contact" className="btn btn-primary w-100 mt-3" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Request a coverage survey">Request a coverage survey</span>
                                    </Link>
                                    <ToolLeadCapture
                                        tool="DAS / ERCCS Checker"
                                        summary={`${exempt ? 'Likely exempt' : 'Likely required'} — occupancy ${occupancy}, floor area ${area}, ${subterranean ? 'has subterranean levels' : 'no subterranean levels'}`}
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="small text-muted mt-3" style={{ maxWidth: 860 }}>
                            Source: International Fire Code / California Fire Code §510, Emergency Responder Radio Coverage. This is
                            a planning guide — the final determination is made by your local fire AHJ, which may adopt stricter local
                            amendments. A radio coverage survey is the definitive test. BUILDLAB performs DAS/ERCCS site surveys and
                            designs the system in-house.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default DasErccsCheckerPage;
